import { useEffect, useState } from "react";
import { ShieldCheck, RefreshCw, Users, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { LEGAL_VERSION } from "../lib/legal";

/**
 * Moderator dashboard: how many active users have accepted the current
 * LEGAL_VERSION. Use this to decide when enforcement (blocking access until
 * accepted) is safe.
 */
export default function AdminConsent() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/legal/consent-stats", { params: { current_version: LEGAL_VERSION } });
      setStats(data);
    } catch { toast.error("Could not load consent stats"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10" data-testid="admin-consent-page">
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <h1 className="font-heading font-900 text-3xl sm:text-4xl flex items-center gap-3">
          <ShieldCheck className="w-8 h-8 text-[#0e7490]" /> Legal Consent Analytics
        </h1>
        <button
          onClick={load}
          disabled={loading}
          data-testid="admin-consent-refresh"
          className="inline-flex items-center gap-1.5 px-3.5 py-2 border border-zinc-300 text-sm font-700 text-zinc-700 hover:bg-zinc-50 disabled:opacity-60"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      <p className="text-muted-foreground mb-6">
        Measuring against current Terms &amp; Privacy version{" "}
        <span className="font-mono text-zinc-800">{LEGAL_VERSION}</span>. Enforcement is safe when the
        active-user acceptance rate is high enough for your rollout plan.
      </p>

      {!stats ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="admin-consent-cards">
            <StatCard label="All users" value={stats.total_users} icon={<Users className="w-5 h-5" />} />
            <StatCard
              label="On current version"
              value={stats.on_current}
              sub={`${stats.percent_on_current}% of all`}
              tone="green"
            />
            <StatCard
              label="Active last 30 days"
              value={stats.active_last_30d}
              icon={<TrendingUp className="w-5 h-5" />}
            />
            <StatCard
              label="Active & on current"
              value={stats.active_on_current}
              sub={`${stats.active_percent_on_current}% of active`}
              tone="cyan"
              testid="admin-consent-active-on-current"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <BreakdownCard label="Outdated acceptance" value={stats.outdated}
              description="Users who accepted a previous version — will see ConsentRePrompt on next visit." />
            <BreakdownCard label="Never accepted" value={stats.never_accepted}
              description="Users created before consent tracking or who dismissed every prompt." />
          </div>

          <section data-testid="admin-consent-versions">
            <h2 className="font-heading font-700 text-lg mb-3">Version history</h2>
            {stats.versions.length === 0 ? (
              <p className="text-sm text-muted-foreground">No versions accepted yet.</p>
            ) : (
              <div className="border border-zinc-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-wide text-zinc-500">
                    <tr>
                      <th className="text-left px-4 py-2">Version</th>
                      <th className="text-right px-4 py-2">Users</th>
                      <th className="text-right px-4 py-2">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.versions.map((v) => {
                      const share = stats.total_users ? ((v.count / stats.total_users) * 100).toFixed(1) : "0.0";
                      const isCurrent = v.version === stats.current_version;
                      return (
                        <tr key={v.version} className="border-t border-zinc-100" data-testid={`admin-consent-version-${v.version}`}>
                          <td className="px-4 py-2 font-mono">
                            {v.version}
                            {isCurrent && <span className="ml-2 text-xs bg-emerald-100 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 font-700 uppercase tracking-wide">Current</span>}
                          </td>
                          <td className="px-4 py-2 text-right font-mono">{v.count.toLocaleString()}</td>
                          <td className="px-4 py-2 text-right font-mono">{share}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, icon, tone, testid }) {
  const toneCls = tone === "green" ? "border-emerald-300 text-emerald-800"
    : tone === "cyan" ? "border-[#0e7490] text-[#0e7490]"
    : "border-zinc-300 text-zinc-800";
  return (
    <div className={`border bg-white p-4 ${toneCls}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wide text-muted-foreground">
        {icon}{label}
      </div>
      <div className="font-heading font-900 text-3xl mt-1">{Number(value || 0).toLocaleString()}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function BreakdownCard({ label, value, description }) {
  return (
    <div className="border border-amber-200 bg-amber-50/40 p-4">
      <div className="text-xs font-mono uppercase tracking-wide text-amber-800">{label}</div>
      <div className="font-heading font-900 text-3xl text-amber-900 mt-1">{Number(value || 0).toLocaleString()}</div>
      <p className="text-xs text-amber-900/80 mt-1 leading-relaxed">{description}</p>
    </div>
  );
}
